import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaWhatsapp, FaRss, FaPhone, FaEnvelope, FaMapMarkerAlt, FaCheckCircle } from 'react-icons/fa';
import { doc, collection, query, where, getDocs, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './Footer.css';

const Footer = () => {
  const [newsEmail, setNewsEmail] = useState('');
  const [newsError, setNewsError] = useState('');
  const [newsSuccess, setNewsSuccess] = useState('');
  const [newsLoading, setNewsLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsError('');
    setNewsSuccess('');

    const email = newsEmail.trim();
    if (!email) {
      setNewsError('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setNewsError('Please enter a valid email address');
      return;
    }

    setNewsLoading(true);

    try {
      // Check if email already exists
      const subscribersRef = collection(db, 'newsletter');
      const q = query(subscribersRef, where('email', '==', email));
      const existing = await getDocs(q);

      if (!existing.empty) {
        setNewsError('This email is already subscribed');
        setNewsLoading(false);
        return;
      }

      // Add new subscriber
      const subscriberId = email.replace(/[^a-zA-Z0-9]/g, '_');
      await setDoc(doc(db, 'newsletter', subscriberId), {
        email: email,
        subscribedAt: new Date().toISOString(),
        status: 'active'
      });

      setNewsSuccess('Thank you! You have been subscribed to our newsletter.');
      setNewsEmail('');
      
      // Clear messages after 5 seconds
      setTimeout(() => {
        setNewsSuccess('');
      }, 5000);
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setNewsError('Failed to subscribe. Please try again.');
    } finally {
      setNewsLoading(false);
    }
  };

  return (
    <footer className="footer">
      {/* Newsletter Section */}
      <div className="newsletter-section">
        <div className="container">
          <div className="newsletter-content">
            <div className="newsletter-text">
              <h3>Subscribe to Our Newsletter</h3>
              <p>Get the latest updates on new products and upcoming sales</p>
            </div>
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input 
                type="email" 
                placeholder="Enter your email address"
                value={newsEmail}
                onChange={(e) => setNewsEmail(e.target.value)}
                disabled={newsLoading}
              />
              <button type="submit" className="btn btn-primary" disabled={newsLoading}>
                {newsLoading ? 'Subscribing...' : 'Subscribe'}
              </button>
              {newsError && <p className="newsletter-message newsletter-error">{newsError}</p>}
              {newsSuccess && <p className="newsletter-message newsletter-success"><FaCheckCircle /> {newsSuccess}</p>}
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="footer-main">
        <div className="container">
          <div className="footer-grid">
            {/* Contact Info */}
            <div className="footer-column">
              <div className="footer-logo">
                <img src="/logo.png" alt="Haksaltech Logo" />
              </div>
              <p className="footer-description">
                Your One Stop Shop for Networking, Security & Surveillance, Telephony and Renewable Energy Solutions in Nigeria.
              </p>
              <div className="footer-contact">
                <div className="contact-item">
                  <FaPhone className="contact-icon" />
                  <span>+234-9012222220, +234-7048312476</span>
                </div>
                <div className="contact-item">
                  <FaEnvelope className="contact-icon" />
                  <span>info@haksaltech.com</span>
                </div>
                <div className="contact-item">
                  <FaMapMarkerAlt className="contact-icon" />
                  <span>Lagos, Nigeria</span>
                </div>
              </div>
              <div className="social-links">
                <a href="https://facebook.com" className="social-link" target="_blank" rel="noopener noreferrer">
                  <FaFacebookF />
                </a>
                <a href="https://wa.me/2347048312476" className="social-link whatsapp" target="_blank" rel="noopener noreferrer">
                  <FaWhatsapp />
                </a>
                <a href="/feed" className="social-link rss">
                  <FaRss />
                </a>
              </div>
            </div>

            {/* Categories */}
            <div className="footer-column">
              <h4 className="footer-title">Categories</h4>
              <ul className="footer-links">
                <li><Link to="/category/networking">Networking</Link></li>
                <li><Link to="/category/smart-home">Smart Home Systems</Link></li>
                <li><Link to="/category/renewable-energy">Renewable Energy</Link></li>
                <li><Link to="/category/security-surveillance">Security & Surveillance</Link></li>
                <li><Link to="/category/fiber-optics">Fiber Optics</Link></li>
                <li><Link to="/category/telephony">Telephony</Link></li>
              </ul>
            </div>

            {/* Brands */}
            <div className="footer-column">
              <h4 className="footer-title">Popular Brands</h4>
              <ul className="footer-links">
                <li><Link to="/shop?brand=cambium">Cambium</Link></li>
                <li><Link to="/shop?brand=cisco">Cisco</Link></li>
                <li><Link to="/shop?brand=grandstream">Grandstream</Link></li>
                <li><Link to="/shop?brand=hikvision">Hikvision</Link></li>
                <li><Link to="/shop?brand=mikrotik">Mikrotik</Link></li>
                <li><Link to="/shop?brand=ubiquiti">Ubiquiti</Link></li>
              </ul>
            </div>

            {/* Useful Links */}
            <div className="footer-column">
              <h4 className="footer-title">Useful Links</h4>
              <ul className="footer-links">
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                <li><Link to="/returns">Returns</Link></li>
                <li><Link to="/terms-conditions">Terms & Conditions</Link></li>
                <li><Link to="/contact">Contact Us</Link></li>
                <li><Link to="/news">Latest News</Link></li>
                <li><Link to="/sitemap">Our Sitemap</Link></li>
              </ul>
            </div>

            {/* Account */}
            <div className="footer-column">
              <h4 className="footer-title">My Account</h4>
              <ul className="footer-links">
                <li><Link to="/my-account">My Account</Link></li>
                <li><Link to="/track-order">Track Your Order</Link></li>
                <li><Link to="/shop">Shop</Link></li>
                <li><Link to="/cart">Shopping Cart</Link></li>
                <li><Link to="/store-locator">Store Locator</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <div className="container">
          <div className="footer-bottom-content">
            <p>&copy; {new Date().getFullYear()} Haksaltech. All Rights Reserved.</p>
            <div className="payment-methods">
              <span>We Accept:</span>
              <img src="https://uxwing.com/wp-content/themes/uxwing/download/banking-finance/bank-transfer-icon.png" alt="Bank Transfer" />
              <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSkkEs1vx3rT86Ozc0du7UWzpk_gSGT3W4XRQ&s" alt="Paystack" />
              <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSkkEs1vx3rT86Ozc0du7UWzpk_gSGT3W4XRQ&s" alt="Card Payments" />
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Float Button */}
      <a href="https://wa.me/2347048312476?text=Hello%20Haksaltech%2C%20I%20am%20interested%20in%20your%20products" 
         className="whatsapp-float" 
         target="_blank" 
         rel="noopener noreferrer"
      >
        <FaWhatsapp />
        <span>Chat on WhatsApp</span>
      </a>
    </footer>
  );
};

export default Footer;
